import { Component } from '@angular/core';
import { DataLayerGeojsonPointDWSComponent } from './data-layer-geojson-point-dws.component';
import { MapService } from './map.service';
import { DataLayerService } from './data-layer.service';
import { ConfigService } from './config.service';
import { UserMessageService } from './user-message.service';

declare var L;

/*
 * data-layer-geojson-point-dws-repeater defines a class to display data from data web services.  This filters on data type.
 * The data format is geojson.
 *
 * --------------------------------
 *
 * Within the configuration file, data web services stations can be included:
 * "overlayLayers": [
 * 		{"id": "repeaters",
 * 			"label": "Repeaters",
 * 			"url": "stationSummaries.geojson,
 *		 	"class": "data-layer-geojson-point-dws-repeater",
 			"refresh": true },
 * ...
 * ]
 */

@Component({
	selector: 'data-layer-geojson-point-dws-repeater',
	standalone: false,
	template: `
		<div class="checkboxDiv"><label class="overlay-layer">
		<input type="checkbox" (click)="menuControlSelected($event)">{{htmlElementLabel}}</label>
		<app-single-legend [active]=active [legendUrl]=legendUrl></app-single-legend>
		</div>
		`,
	styleUrls: ['app.component.css']
})
export class DataLayerGeojsonPointDWSRepeaterComponent extends DataLayerGeojsonPointDWSComponent {

	dtAlert1RxCount = 'Alert1RxCount';
	dtAlert1RpCount = 'Alert1RpCount';
	dtAlert2RxCount = 'Alert2RxCount';
	dtAlert2RpCount = 'Alert2RpCount';

	constructor (
		mapService: MapService,
		dataLayerService: DataLayerService,
		configService: ConfigService,
		userMessageService: UserMessageService ) {

		super (mapService, dataLayerService, configService, userMessageService );
		this.htmlElementLabel = 'DWS';
		this.dataType = 'Alert2TxCount';
		this.statisticType = 'Last';
		this.duration = '1day';
		this.title = 'statistics.statisticValue';
	}

	setData ( someData: any ) {
		// Set the standard data
		super.setData(someData);
	}


	// Parses the stationSummaries.geojson file, filters the features so only those containing specified dataType
	// such as "Battery Voltage" are included and stores the results in this.value.
	// Information is added to this layer so station has a title and a NovaScore at the main level.
	// This is used in the map display.
	parseDataFile () {
		const self = this;
		let unitsString = '';

		return new Promise(function(resolve, reject) {

			const request = new XMLHttpRequest();
			request.onload = function ( ) {
				if ( request.readyState === request.DONE ) {
					if ( request.status === 200 ) {
						// first, get geojson file content
						const dwsStations = JSON.parse(request.responseText);

						// filter the features to only include those containing specified dataTypes
						dwsStations.features = dwsStations.features.filter(function(s) {
							const dt = s.properties.stationSummary.dataTypes;
							const dt_filtered = dt.filter(function (d) { return d.name === self.dataType; });
							return dt_filtered.length === 0 ? false : true; });

						// If station is out of service, set dwsTitle to "OOS" and novaScore to -1
						// If station has no report, set dwsTitle to "NR" and novaScore to 0

						// first set the novaScores array
						const novaScores = self.parseAndSetNovaScores( dwsStations );
						const alarmTriggers = self.parseAndSetAlarmTriggers (dwsStations);

						dwsStations.features.forEach(function(item, index) {
							if ( item.properties.stationSummary.outOfService === true ) {
									item.properties.stationSummary.dwsTitle = 'OOS';
									item.properties.stationSummary.dwsNovaScore = -1;
									item.properties.stationSummary.tooltipContent =
										'<span class=\'tooltipHeader\'>' + item.properties.stationSummary.name + '</span>' +
										'<br>Out of service';
							} else {
								// If desired title is part of the station info, set it now.
								if ( self.title.startsWith('station')) {
									const parts = self.title.split('.');
									if ( parts.length === 3 ) {
										// Something like station.id.0 meaning stationSummary.id rounded to 0 decimals
										item.properties.stationSummary.dwsTitle = item.properties.stationSummary[parts[1]].toFixed(Number(parts[2]).toFixed());
									} else if ( parts.length === 2 ) {
										// Something like station.name meaning stationSummary.name
										item.properties.stationSummary.dwsTitle = item.properties.stationSummary[parts[1]];
									}
								}

								const tmpStatistic = item.properties.stationSummary.statistics.filter(function(stat) {
									return stat.statisticType.toUpperCase() === self.statisticType.toUpperCase() &&
										stat.duration != null &&
										stat.duration.toUpperCase() === self.duration.toUpperCase();
								});

								// Get the alert2TXCount to start with
								const alert2TxCount = tmpStatistic.filter(function(stat) {
									return stat.dataType.toUpperCase() === self.dataType.toUpperCase()
								});

								const tmpS = ( alert2TxCount.length > 0 && alert2TxCount[0].statisticValue != null) ? alert2TxCount[0] : null;

								// reports transmitted is our key statistic
								// if that is null, none of the other data
								// types will be of interest
								if ( tmpS != null ) {
									// Get all the remaining datatypes desired
									const alert1RxCount = tmpStatistic.filter(function(stat) {
										return stat.dataType.toUpperCase() === self.dtAlert1RxCount.toUpperCase()
									});
									const alert1RpCount = tmpStatistic.filter(function(stat) {
										return stat.dataType.toUpperCase() === self.dtAlert1RpCount.toUpperCase()
									});
									const alert2RxCount = tmpStatistic.filter(function(stat) {
										return stat.dataType.toUpperCase() === self.dtAlert2RxCount.toUpperCase()
									});
									const alert2RpCount = tmpStatistic.filter(function(stat) {
										return stat.dataType.toUpperCase() === self.dtAlert2RpCount.toUpperCase()
									});

									const alert2TxCountLine = self.createStatisticLine(
										'ALERT2 reports transmitted', alert2TxCount);
									const alert1RxCountLine = self.createStatisticLine(
										'ALERT1 reports received', alert1RxCount);
									const alert1RpCountLine = self.createStatisticLine(
										'ALERT1 reports repeated', alert1RpCount);
									const alert2RxCountLine = self.createStatisticLine(
										'ALERT2 reports received', alert2RxCount);
									const alert2RpCountLine = self.createStatisticLine(
										'ALERT2 reports repeated', alert2RpCount);

									const novaScore = self.convertNovaScore( novaScores, tmpS.novaScore );
									item.properties.stationSummary.dwsNovaScore = novaScore;
									const alarmsMessage = self.createAlarmMessage ( item.properties.stationSummary['alarms'] );

									// If desired title is part of the station info, set it now.
									if ( self.title.startsWith('statistics')) {
										const parts = self.title.split('.');
										if ( parts.length === 3 ) {
											// Something like statistics.statisticValue.0 meaning stationSummary.statistics.statisticValue rounded to 0 decimals
											item.properties.stationSummary.dwsTitle = tmpS[parts[1]].toFixed(Number(parts[2]).toFixed());
										} else if ( parts.length === 2 ) {
											item.properties.stationSummary.dwsTitle = tmpS[parts[1]];
										}
									}

									item.properties.stationSummary.tooltipContent =
										'<span class=\'tooltipHeader\'>' + item.properties.stationSummary.name + ', id: ' +
										item.properties.stationSummary.id + ', numId: ' + item.properties.stationSummary.numId +
										'</span>' +
										'<br>Last reported daily repeater values:' +
										'<br><span class=\'tooltipContent\'>' + 
										'<br>' + alert2TxCountLine +
										'<br>' + alert1RxCountLine +
										'<br>' + alert1RpCountLine +
										'<br>' + alert2RxCountLine +
										'<br>' + alert2RpCountLine +
										'<br>' + alarmsMessage +
										'</span>';
								} else { // no report but still in service
									item.properties.stationSummary.dwsTitle = 'NR';
									item.properties.stationSummary.dwsNovaScore = 0;
									item.properties.stationSummary.tooltipContent =
										'<span class=\'tooltipHeader\'>' + item.properties.stationSummary.name + '</span><br>' +
										'id: ' + item.properties.stationSummary.numId + '<br>' +
										'No report';
								}
							}
						});
						// console.log(dwsStations);
						self.value = dwsStations;
						self.valueObservable.next(dwsStations);
						console.log ( '[' + new Date().toLocaleTimeString() + '] Reading ' +
							self.url + ' for all ' + self.id + ' stations was successfully completed.');

						resolve(true);
					} else {	// request status wasn't 200
						console.log ( '[' + new Date().toLocaleTimeString() + '] Reading ' + uri + ' for DWS was UNSUCCESSFUL.');
						reject(Error(request.statusText));
					}

				}
			};

			const uri = self.configService.createURI ( { 'filename': self.url });
			request.open('get', uri, true );
			console.log('[' + new Date().toLocaleTimeString() + '] Attempting to read ' + uri + ' for DWS data.');
			request.send();
		});

	}

	createStatisticLine(description, statistic) {
		const tmpS = ( statistic.length > 0 && statistic[0].statisticValue != null) ? 
			statistic[0] : null;
		return tmpS == null ?  'No reports' :
			tmpS.statisticValue + ' ' +
			description +
			' in the hour prior to ' +
			tmpS.statisticDateTime;
	}

}
